import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { FaShare, FaUserCheck, FaUserPlus } from 'react-icons/fa';
import { GoHeartFill } from "react-icons/go";
import { BiSolidMessageSquareDetail } from "react-icons/bi";
import { IoBookmark } from "react-icons/io5";
import { GrView } from "react-icons/gr";
import { useStatusContext } from '../utils/StatusContext';
import { useFollow } from '../utils/FollowContext';
import defaultAvatar from '../assets/svg/images/default-avatar.jpg';
import VerificationBadge from '../components/VerificationBadge';
import { toast } from 'react-toastify';
import CommentSection from '../components/CommentSection';
import Modal from 'react-modal';

Modal.setAppElement('#root');

export default function StatusPostItem({ status }) {
  const auth = getAuth();
  const navigate = useNavigate();
  const { viewedStatuses, markStatusAsViewed, currentUser } = useStatusContext();
  const {
    isFollowing,
    shouldFollowBack,
    toggleFollow,
    followers,
    isInitialized
  } = useFollow();
  const [userData, setUserData] = useState({
    profileImage: defaultAvatar,
    name: 'Unknown',
    username: 'unknown',
    followersCount: 0,
    verified: false
  });
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const [imageError, setImageError] = useState({});
  const [likeAnimation, setLikeAnimation] = useState(false);
  const [saveAnimation, setSaveAnimation] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const hasViewed = viewedStatuses.has(status.id);

  useEffect(() => {
    async function fetchUserData() {
      try {
        const userRef = doc(db, 'users', status.userRef);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          setUserData({
            profileImage: data.photoURL || defaultAvatar,
            name: data.name || 'Unknown',
            username: data.username || 'unknown',
            followersCount: data.followers?.length || 0,
            verified: data.verified || false
          });

          if (currentUser) {
            setIsLiked(status.likedBy?.includes(currentUser.uid));
            setIsSaved(status.savedBy?.includes(currentUser.uid));
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    }
    fetchUserData();
  }, [status.userRef, status.likedBy, status.savedBy, currentUser, followers]);

  const createNotification = async (type, recipientId, message, targetId = null) => {
    if (!currentUser || currentUser.uid === recipientId) return;

    try {
      const notifRef = collection(db, `notifications/${recipientId}/userNotifications`);
      await addDoc(notifRef, {
        type,
        senderId: currentUser.uid,
        targetId,
        message,
        timestamp: serverTimestamp(),
        isRead: false
      });
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  const handleFollow = async (e) => {
    e.stopPropagation();
    if (!currentUser) {
      navigate('/sign-in');
      return;
    }

    setIsFollowLoading(true);
    try {
      const wasFollowing = isFollowing(status.userRef);
      await toggleFollow(status.userRef);
      if (!wasFollowing) {
        const message = `started following you.`;
        await createNotification('follow', status.userRef, message);
      }
    } catch (error) {
      console.error('Error handling follow:', error);
    } finally {
      setIsFollowLoading(false);
    }
  };

  const handleLike = async (e) => {
    e.stopPropagation();
    if (!currentUser) {
      navigate('/sign-in');
      return;
    }

    try {
      setLikeAnimation(true);
      setTimeout(() => setLikeAnimation(false), 1000);
      
      const statusRef = doc(db, 'status', status.id);
      if (isLiked) {
        await updateDoc(statusRef, {
          likedBy: arrayRemove(currentUser.uid)
        });
        setIsLiked(false);
      } else {
        await updateDoc(statusRef, {
          likedBy: arrayUnion(currentUser.uid)
        });
        setIsLiked(true);
        const statusExcerpt = status.text ? status.text.slice(0, 50) + (status.text.length > 50 ? '...' : '') : 'No caption';
        const message = `liked your status: ${statusExcerpt}.`;
        await createNotification('like', status.userRef, message, status.id);
      }
    } catch (error) {
      console.error('Error handling like:', error);
    }
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    if (!currentUser) {
      navigate('/sign-in');
      return;
    }

    try {
      setSaveAnimation(true);
      setTimeout(() => setSaveAnimation(false), 1000);
      
      const statusRef = doc(db, 'status', status.id);
      if (isSaved) {
        await updateDoc(statusRef, {
          savedBy: arrayRemove(currentUser.uid)
        });
        setIsSaved(false);
      } else {
        await updateDoc(statusRef, {
          savedBy: arrayUnion(currentUser.uid)
        });
        setIsSaved(true);
        const statusExcerpt = status.text ? status.text.slice(0, 50) + (status.text.length > 50 ? '...' : '') : 'No caption';
        const message = `saved your status: ${statusExcerpt}.`;
        await createNotification('save', status.userRef, message, status.id);
      }
    } catch (error) {
      console.error('Error handling save:', error);
    }
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    
    const shareUrl = `${window.location.origin}/status/${status.id}`;
    const shareData = {
      title: `${userData.name}'s Status`,
      text: `${userData.name} shared: ${status.text ? status.text.slice(0, 50) + (status.text.length > 50 ? '...' : '') : 'Check out this status!'}`,
      url: shareUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.info('Status link copied to clipboard! Share it manually.');
      }
    } catch (error) {
      console.error('Error sharing status:', error);
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.info('Status link copied to clipboard! Share it manually.');
      } catch (clipboardError) {
        console.error('Error copying to clipboard:', clipboardError);
        toast.error('Failed to share status. Please share the link manually.');
      }
    }
  };

  const handleViewStatus = async () => {
    if (!currentUser) {
      navigate('/sign-in');
      return;
    }

    setIsViewing(true);
    try {
      if (!hasViewed) {
        const statusRef = doc(db, 'status', status.id);
        await updateDoc(statusRef, {
          viewedBy: arrayUnion(currentUser.uid)
        });
        await markStatusAsViewed(status.id);
      }
      navigate(`/status/${status.id}/full`);
    } catch (error) {
      console.error('Error viewing status:', error);
    } finally {
      setIsViewing(false);
    }
  };

  const handleProfileClick = (e) => {
    e.stopPropagation();
    navigate(`/profile/${status.userRef}`);
  };

  const handleImageError = (index) => {
    setImageError(prev => ({ ...prev, [index]: true }));
  };

  const handleComment = () => {
    if (!currentUser) {
      toast.error("Please sign in to comment");
      return;
    }
    setShowComments(!showComments);
  };

  const getFollowButtonState = () => {
    if (!isInitialized) {
      return {
        text: '...',
        className: 'bg-gray-500 text-white',
        icon: <FaUserPlus className="mr-1" />
      };
    }
    
    if (isFollowing(status.userRef)) {
      return {
        text: 'Following',
        className: 'bg-black text-white hover:bg-gray-800',
        icon: <FaUserCheck className="mr-1" />
      };
    }
    if (shouldFollowBack(status.userRef)) {
      return {
        text: 'Follow Back',
        className: 'bg-blue-500 text-white hover:bg-blue-600',
        icon: <FaUserPlus className="mr-1" />
      };
    }
    return {
      text: 'Follow',
      className: 'bg-white text-black hover:bg-gray-100',
      icon: <FaUserPlus className="mr-1" />
    };
  };

  const followState = getFollowButtonState();

  if (!isInitialized) {
    return (
      <div className="bg-black text-white border-b border-gray-700 py-4 px-4 sm:px-0">
        <div className="flex items-center mb-2">
          <div className="w-12 h-12 rounded-full bg-gray-700 animate-pulse mr-3"></div>
          <div className="flex-1">
            <div className="h-4 w-24 bg-gray-700 rounded animate-pulse mb-1"></div>
            <div className="h-3 w-16 bg-gray-700 rounded animate-pulse"></div>
          </div>
          <div className="h-8 w-20 bg-gray-700 rounded-full animate-pulse"></div>
        </div>
        <div className="h-64 bg-gray-700 rounded-2xl animate-pulse mb-3"></div>
        <div className="flex items-center mt-3 space-x-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-5 w-5 bg-gray-700 rounded-full animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white border-b border-gray-700 py-4 px-4 sm:px-0">
      <style jsx>{`
        @keyframes likeBounce {
          0%, 100% { transform: scale(1); }
          25% { transform: scale(1.2); }
          50% { transform: scale(0.9); }
          75% { transform: scale(1.1); }
        }

        @keyframes saveBounce {
          0%, 100% { transform: scale(1); }
          25% { transform: scale(1.2) rotate(-10deg); }
          50% { transform: scale(0.9); }
          75% { transform: scale(1.1) rotate(10deg); }
        }
      `}</style>

      {/* First Row: Profile, Name, Username, Follow Button */}
      <div className="flex items-center mb-2">
        <button
          onClick={handleProfileClick}
          className={`w-12 h-12 rounded-full p-[2px] mr-3 focus:outline-none flex-shrink-0 ${
            hasViewed
              ? 'bg-gray-500'
              : 'bg-gradient-to-r from-[#FF1493] via-[#A020F0] to-[#FF8C00]'
          }`}
          aria-label={`View ${userData.name}'s profile`}
          disabled={isViewing}
        >
          <img
            src={userData.profileImage}
            alt={`${userData.name}'s profile`}
            className="w-full h-full rounded-full object-cover border-2 border-gray-900"
            onError={(e) => (e.target.src = defaultAvatar)}
          />
        </button>

        <div className="flex-1 min-w-0" onClick={handleProfileClick}>
          <div className="flex items-center">
            <p className="font-bold text-white truncate max-w-[120px] sm:max-w-[180px]">
              {userData.name}
            </p>
            <VerificationBadge isVerified={userData.verified} />
          </div>
          <p className="text-gray-500 text-sm truncate max-w-[120px] sm:max-w-[180px]">@{userData.username}</p>
        </div>

        {currentUser?.uid !== status.userRef && (
          <button
            onClick={handleFollow}
            disabled={isFollowLoading || isViewing}
            className={`px-4 py-1 rounded-full font-medium transition duration-300 focus:outline-none flex items-center ${
              followState.className
            } ${isFollowLoading ? 'opacity-70' : ''} flex-shrink-0 ml-2`}
            aria-label={`${followState.text} ${userData.name}`}
          >
            {followState.icon}
            {isFollowLoading ? '...' : followState.text}
          </button>
        )}
      </div>

      {/* Content Area: Images (professionally sized like Twitter) and Text */}
      <div 
        onClick={handleViewStatus} 
        className="cursor-pointer"
        aria-disabled={isViewing}
      >
        {status.imageUrls?.length > 0 && (
          <div className="mb-3">
            {status.imageUrls.length === 1 ? (
              imageError[0] ? (
                <div className="w-full aspect-[16/9] bg-gray-700 rounded-2xl flex items-center justify-center">
                  <span className="text-gray-900"></span>
                </div>
              ) : (
                <img
                  src={status.imageUrls[0]}
                  alt="Status image"
                  className="w-full aspect-[16/9] object-cover rounded-2xl"
                  onError={() => handleImageError(0)}
                />
              )
            ) : status.imageUrls.length === 2 ? (
              <div className="grid grid-cols-2 gap-1">
                {status.imageUrls.slice(0, 2).map((url, index) => (
                  imageError[index] ? (
                    <div key={index} className="aspect-[4/3] bg-gray-700 rounded-2xl flex items-center justify-center">
                      <span className="text-gray-900"></span>
                    </div>
                  ) : (
                    <img
                      key={index}
                      src={url}
                      alt={`Status image ${index + 1}`}
                      className="w-full h-full aspect-[4/3] object-cover rounded-2xl"
                      onError={() => handleImageError(index)}
                    />
                  )
                ))}
              </div>
            ) : status.imageUrls.length === 3 ? (
              <div className="grid grid-cols-2 grid-rows-2 gap-1">
                {status.imageUrls.slice(0, 3).map((url, index) => (
                  imageError[index] ? (
                    <div 
                      key={index}
                      className={`${index === 0 ? 'row-span-2 aspect-[9/16]' : 'aspect-[16/9]'} bg-gray-700 rounded-2xl flex items-center justify-center`}
                    >
                      <span className="text-gray-900"></span>
                    </div>
                  ) : (
                    <img
                      key={index}
                      src={url}
                      alt={`Status image ${index + 1}`}
                      className={`w-full h-full object-cover rounded-2xl ${
                        index === 0 ? 'row-span-2 aspect-[9/16]' : 'aspect-[16/9]'
                      }`}
                      onError={() => handleImageError(index)}
                    />
                  )
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 grid-rows-2 gap-1">
                {status.imageUrls.slice(0, 4).map((url, index) => (
                  imageError[index] ? (
                    <div key={index} className="aspect-square bg-gray-700 rounded-2xl flex items-center justify-center">
                      <span className="text-gray-900"></span>
                    </div>
                  ) : (
                    <img
                      key={index}
                      src={url}
                      alt={`Status image ${index + 1}`}
                      className="w-full h-full aspect-square object-cover rounded-2xl"
                      onError={() => handleImageError(index)}
                    />
                  )
                ))}
              </div>
            )}
            {status.imageUrls.length > 4 && (
              <div className="mt-1 text-gray-500 text-sm">
                +{status.imageUrls.length - 4} more
              </div>
            )}
          </div>
        )}

        {status.text && (
          <p className="text-white text-base leading-6 line-clamp-3">{status.text}</p>
        )}
      </div>

      {/* Action Buttons and View Count */}
      <div className="flex items-center justify-between mt-3">
        {/* Action Buttons Group */}
        <div className="flex items-center space-x-6">
          {/* Like Button */}
          <button
            onClick={handleLike}
            className={`text-gray-500 hover:text-red-400 transition duration-300 focus:outline-none ${
              isLiked ? 'text-red-500' : ''
            }`}
            aria-label={isLiked ? 'Unlike status' : 'Like status'}
            disabled={isViewing}
          >
            <GoHeartFill 
              size={18} 
              className={likeAnimation ? "animate-[likeBounce_1s_ease-in-out]" : ""} 
            />
          </button>

          {/* Comment Button */}
          <button
            onClick={handleComment}
            className="text-gray-500 hover:text-gray-500 transition duration-300 focus:outline-none"
            aria-label="Comment on status"
            disabled={isViewing}
          >
            <BiSolidMessageSquareDetail size={18} />
          </button>

          {/* Save Button */}
          <button
            onClick={handleSave}
            className={`text-gray-500 hover:text-yellow-400 transition duration-300 focus:outline-none ${
              isSaved ? 'text-yellow-500' : ''
            }`}
            aria-label={isSaved ? 'Unsave status' : 'Save status'}
            disabled={isViewing}
          >
            <IoBookmark 
              size={18} 
              className={saveAnimation ? "animate-[saveBounce_1s_ease-in-out]" : ""} 
            />
          </button>

          {/* Share Button */}
          <button
            onClick={handleShare}
            className="text-gray-500 hover:text-gray-500 transition duration-300 focus:outline-none"
            aria-label="Share status"
            disabled={isViewing}
          >
            <FaShare size={18} />
          </button>
        </div>

        {/* View Count at the Right End */}
        <div className="flex items-center text-gray-500 ml-auto">
          <GrView size={18} className="mr-1" />
          <span>{status.viewedBy?.length || 0}</span>
        </div>
      </div>

      {/* Comment Section in Modal */}
      <Modal
        isOpen={showComments}
        onRequestClose={() => setShowComments(false)}
        style={{
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#1a1a1a',
            border: 'none',
            borderRadius: '8px',
            padding: '20px',
            maxHeight: '80vh',
            overflowY: 'auto',
            width: '90%',
            maxWidth: '500px',
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            zIndex: 1000,
          },
        }}
        contentLabel="Comment Modal"
      >
        <CommentSection contentId={status.id} contentType="status" />
        <button
          onClick={() => setShowComments(false)}
          className="mt-4 px-4 py-2 bg-pink-500 text-white rounded hover:bg-pink-600"
        >
          Close
        </button>
      </Modal>
    </div>
  );
}